// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';
import { DistributionValue, DistributionTick } from '../../../../interfaces/hce/hce-stats';

@Component({
  selector: 'app-hce-stats-histogram',
  standalone: true,
  imports: [NgxEchartsDirective], // use echarts [options]="opt
  providers: [provideEcharts()], // Provide ECharts within this component
  templateUrl: './hce-stats-histogram.component.html',
  styleUrls: ['./hce-stats-histogram.component.css']
})
export class HceStatsHistogramComponent implements OnChanges {
  /* 
   * chart
   */
  @Input() title?: string;
  @Input() width: string = '600px';
  @Input() height: string = '460px'; // including the chart legend
  @Input() barColor = '#FFFFFF';

  /* bar */
  @Input() barWidthPx = 10;

  /* 
   * chart data
   */
  @Input() data: DistributionValue = [];

  /* 
   * X-axis
   */

  /* axis label */
  @Input() xAxisTitle = 'x-axis';

  /* xTicks */
  @Input() xTicks?: DistributionTick = [];

  /* 
   * Y-axis
   */

  /* axis label */
  @Input() yAxisTitle = 'y-axis';

  /* yTicks: Give highest priority if specified */
  @Input() yTicks?: DistributionTick = undefined;

  /* yMin / yMax */
  @Input() yMin?: number | 'dataMin';
  @Input() yMax?: number | 'dataMax';

  /* Y-axis division count: Used only when yTicks is not specified */
  @Input() ySplitNumber = 11;

  chartOption: any = {};

  ngOnChanges(_: SimpleChanges): void {
    this.buildOptions();
  }

  ngOnInit(): void {
    this.buildOptions();
  }

  private buildOptions(): void {
    /*
     * X-axis: category axis (for display) based on xTicks + value axis (for plotting)
    */
    const rawTicks = Array.isArray(this.xTicks) ? this.xTicks : [];
    const ticks = rawTicks
      .map((t) => this.toNumber(t))
      .filter((n): n is number => n != null)
      .sort((a, b) => a - b);

    if (ticks.length === 0) {
      this.chartOption = {}; // Nothing can be drawn
      return;
    }

    // xMin/xMax Read & format
    let xMinNum = 0;
    let xMaxNum = 0;
    if (ticks?.length > 0) {
      xMinNum = ticks[0];
      xMaxNum = ticks[ticks.length - 1];
    }

    const ticksInRange = ticks.filter((v) =>
      (xMinNum == null || v >= xMinNum)
    );
    const labels = ticksInRange.map(String);

    /*
     * Normalize data (handle x values outside the range)
    */
    const minX = ticksInRange[0] ?? ticks[0];
    const maxX = ticksInRange[ticksInRange.length - 1] ?? ticks[ticks.length - 1];
    const customData: { value: [number, number]; rawX: number }[] = [];

    for (const row of this.data) {
      const xv0 = this.toNumber(row[0]);
      const yv  = this.toNumber(row[1]);
      if (xv0 == null || yv == null) {
        continue;
      }

      const xv = Math.min(Math.max(xv0, minX), maxX);
      customData.push({ value: [xv, yv], rawX: xv0 });
    }

    /*
     * Draw bars (width fixed to barWidthPx)
    */
    const renderItem = (_params: any, api: any) => {
      const xVal = api.value(0);
      const yVal = api.value(1);

      const coordTop = api.coord([xVal, yVal]); // Value axis at xAxisIndex: 1
      const coordBase = api.coord([xVal, 0]);

      const x = coordTop[0] - this.barWidthPx / 2;
      const y = Math.min(coordTop[1], coordBase[1]);
      const h = Math.abs(coordBase[1] - coordTop[1]);

      return {
        type: 'rect',
        z: 5,
        shape: { x, y, width: this.barWidthPx, height: h },
        style: { fill: this.barColor }
      };
    };

    /*
     * Y-axis: addition logic for yTicks / yMin / yMax (yTicks has highest priority)
    */
    const rawYTicks = Array.isArray(this.yTicks) ? this.yTicks : [];
    const yTicksNum = rawYTicks
      .map(v => this.toNumber(v))
      .filter((n): n is number => n != null)
      .sort((a, b) => a - b);

    const yTicksSpecified = yTicksNum.length > 0;

    // Data y-min / y-max
    const yVals = customData.map(d => d.value[1]);
    const dataYMin = yVals.length ? Math.min(...yVals) : 0;
    const dataYMax = yVals.length ? Math.max(...yVals) : 0;

    // Read yMin / yMax, supporting 'dataMin'/'dataMax'; fallback order: yTicks first, then data
    let yMinNum = (this.yMin === 'dataMin') ? dataYMin : this.toNumber(this.yMin ?? null);
    let yMaxNum = (this.yMax === 'dataMax') ? dataYMax : this.toNumber(this.yMax ?? null);

    if (yMinNum == null) yMinNum = yTicksSpecified ? yTicksNum[0] : dataYMin;
    if (yMaxNum == null) yMaxNum = yTicksSpecified ? yTicksNum[yTicksNum.length - 1] : dataYMax;
    if (yMinNum != null && yMaxNum != null && yMinNum > yMaxNum) {
      [yMaxNum, yMinNum] = [yMinNum, yMaxNum];
    }

    // Verify that yTicks are in equal intervals
    let yIntervalFromTicks: number | undefined;
    let useMarkLineForYTicks = false;
    if (yTicksNum.length >= 2) {
      const deltas = yTicksNum.slice(1).map((v, i) => v - yTicksNum[i]);
      const eps = 1e-9;
      const allSame = deltas.every(d => Math.abs(d - deltas[0]) < eps);
      if (allSame) {
        yIntervalFromTicks = deltas[0];
      } else {
        // If yTicks are not evenly spaced, render horizontal reference lines and values via ECharts markLine
        useMarkLineForYTicks = true;
      }
    } else if (yTicksNum.length === 1) {
      // Single tick → handle as non-uniform; draw with markLine
      useMarkLineForYTicks = true;
    }

    /*
     * markLine (vertical lines = xTicks, horizontal lines = optional yTicks)
    */
    // Vertical lines (xTicks)
    const xMarkLines = ticksInRange.map((t) => ({ xAxis: t }));

    // Horizontal lines (only when yTicks is specified and treated as non-uniform)
    const yMarkLines = (yTicksSpecified && useMarkLineForYTicks)
      ? yTicksNum.map(v => ({
          yAxis: v,
          label: { show: true, formatter: String(v), position: 'start', color: '#666' }
        }))
      : [];

    const markLineData = [...xMarkLines, ...yMarkLines];

    /*
     * Finalize Y-axis min / max / interval
    */
    let finalYMin = yMinNum!;
    let finalYMax = yMaxNum!;
    let finalInterval: number | undefined;
    let axisLabelFormatter: (v: number) => string = (v) =>String(Math.round(v));

    if (yTicksSpecified) {
      // yTicks take highest priority
      if (useMarkLineForYTicks) {
        // Non-uniform (or single tick) → hide axis and display via markLine
        finalYMin = yTicksNum[0];
        finalYMax = yTicksNum[yTicksNum.length - 1];
        finalInterval = undefined;
        axisLabelFormatter = (v) => String(v);
      } else {
        // Uniform intervals → build the axis directly from yTicks
        finalYMin = yTicksNum[0];
        finalYMax = yTicksNum[yTicksNum.length - 1];
        finalInterval = yIntervalFromTicks;

        const allInt = yTicksNum.every((t) => Number.isInteger(t));
        axisLabelFormatter = (v) => allInt ? String(Math.round(v)) : String(v);
      }
    } else {
      // If yTicks is not specified: use integers with a fixed number of splits
      // (if the maximum data value is less than ySplitNumber, split into ySplitNumber steps of 1)
      for (let split = Math.max(1, this.ySplitNumber); split > 0; split--) {
        const aligned = this.alignAndFixSplits(
          yMinNum!, yMaxNum!,
          split
        );
        if (split - Math.ceil(yMaxNum / aligned.interval) <= 1) {
          finalYMin = aligned.min;
          finalYMax = aligned.max;
          finalInterval = aligned.interval;
          break;
        }
      }
      axisLabelFormatter = (v) => String(Math.round(v));
    }

    /*
     * Final Y-axis options
    */
    const yAxisOption: any = {
      type: 'value',
      name: this.yAxisTitle,
      nameLocation: 'middle',
      nameGap: 67,
      min: finalYMin,
      max: finalYMax,
      // splitNumber is a hint; when interval is set, (max - min) / interval becomes the actual number of splits。
      splitNumber: this.ySplitNumber,
      minInterval: (yTicksSpecified ? undefined : 1),
      axisLabel: { formatter: axisLabelFormatter },
      splitLine: { show: true, lineStyle: { color: '#e0e0e0', type: 'solid', width: 1 } }
    };

    if (finalInterval != null) {
      yAxisOption.interval = finalInterval;
    }

    if (yTicksSpecified && useMarkLineForYTicks) {
      // For non-uniform yTicks, hide the axis and render horizontal lines and values via markLine
      yAxisOption.axisLabel.show = false;
      yAxisOption.axisTick = { show: false };
      yAxisOption.splitLine.show = false;
    }

    this.chartOption = {
      animation: false,

      // chart title
      title: this.title ? {
        text: this.title,
        left: 'center',
        top: 8,
        textStyle: { color: 'gray', fontSize: 16, fontWeight: 600 },
      } : undefined,

      tooltip: {
        trigger: 'item',
        formatter: (p: any) => {
          const rawX = p?.data?.rawX ?? '';
          const y = Array.isArray(p?.value) ? p.value[1] : '';
          return `${this.xAxisTitle}: ${rawX}<br/>${this.yAxisTitle}: ${y}`;
        }
      },

      grid: {
        top: this.title ? 56 : 40,
        right: 16,
        bottom: 60,
        left: 80,
        show: true,
        backgroundColor: '#FFFDF6'
      },

      // xAxis[0] : category axis for display (fixed labels)
      // xAxis[1] : value axis for plotting (hidden)
      xAxis: [
        {
          type: 'category',
          name: this.xAxisTitle,
          nameLocation: 'middle',
          nameGap: 42,
          boundaryGap: false,
          data: labels,
          axisLabel: { interval: 0, rotate: 90, margin: 8 },
          axisTick: { alignWithLabel: true },
          min: 0,
          max: labels.length > 0 ? labels.length - 1 : 0
        },
        {
          type: 'value',
          min: xMinNum,
          max: xMaxNum,
          show: false
        }
      ],

      yAxis: yAxisOption,

      series: [
        {
          name: '',
          type: 'custom',
          xAxisIndex: 1, // Draw using continuous coordinates on the value axis
          yAxisIndex: 0,
          encode: { x: 0, y: 1 },
          renderItem,
          data: customData,
          clip: true,

          // Render bars in the foreground
          zlevel: 1,
          z: 5,

          // Hide top labels in all states
          label: { show: false },
          emphasis: { label: { show: false } },
          select: { label: { show: false } },

          // Draw vertical lines (xTicks) and, if needed, horizontal lines (yTicks) in the background
          markLine: (yTicksSpecified && useMarkLineForYTicks) ? {
            symbol: 'none',
            silent: true,
            zlevel: 0,
            z: 1,
            lineStyle: { color: '#e0e0e0', type: 'solid', width: 1 },
            // Labels are hidden by default; only yTicks horizontal lines have show: true set individually
            label: { show: false },
            data: markLineData
          } : undefined
        }
      ]
    };
  }

  private toNumber(v: any): number | null {
    if (v == null) return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  }

  /**
   * Keep ySplitNumber fixed and adjust y-axis min/max/interval to integer values
   * - The maximum must always be interval × ySplitNumber
   * - If the data maximum exceeds this range, choose a new interval
   * - When the data maximum is below ySplitNumber, set the interval to 1 and guarantee ySplitNumber splits
   */
  private alignAndFixSplits(
    yMin: number,
    yMax: number,
    splitNumber: number,
  ) {
    const split = Math.max(1, Math.floor(splitNumber || 6));

    if (!isFinite(yMin)) {
      yMin = 0;
    }
    if (!isFinite(yMax)) {
      yMax = yMin + 1;
    }
    if (yMax < yMin) {
      [yMin, yMax] = [yMax, yMin];
    }

    const minInt = 1;

    // When the data maximum is less than ySplitNumber, set tick step to 1 and guarantee at least `split` splits
    const dataMaxRelative = Math.ceil(yMax - yMin);
    if (dataMaxRelative < split) {
      const step = 1;
      const aMin = Math.floor(yMin);
      const aMax = aMin + step * split;
      return { min: aMin, max: aMax, interval: step };
    }

    // Normal path
    let range = yMax - yMin;
    if (!isFinite(range) || range <= 0) {
      range = 1;
    }

    let rawStep = (range / split);
    rawStep = Math.max(minInt, Math.round(rawStep));

    let step = this.niceIntegerStepNear(rawStep, minInt);
    let aMin = Math.floor(yMin / step) * step;

    const neededPerSplit = Math.ceil((yMax - aMin) / split);
    if (neededPerSplit > step) {
      step = this.niceIntegerStepNear(neededPerSplit, minInt);
      aMin = Math.floor(yMin / step) * step;
    }

    let aMax = aMin + step * split;
    aMin = Math.round(aMin);
    step = Math.max(1, Math.round(step));
    aMax = aMin + step * split;

    return { min: aMin, max: aMax, interval: step };
  }

  private niceIntegerStepNear(raw: number, minInterval?: number): number {
    if (!isFinite(raw) || raw <= 0) raw = 1;

    const pow = Math.pow(10, Math.floor(Math.log10(raw)));
    const base = raw / pow;

    const niceIinSteps = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50];
    let best = niceIinSteps[0];
    let bestDiff = Math.abs(base - best);
    for (let i = 1; i < niceIinSteps.length; i++) {
      const diff = Math.abs(base - niceIinSteps[i]);
      if (diff < bestDiff) {
        best = niceIinSteps[i];
        bestDiff = diff;
      }
    }

    let step = best * pow;
    if (minInterval && minInterval > 0) {
      if (step < minInterval) {
        step = Math.ceil(step / minInterval) * minInterval;
      } else {
        step = Math.round(step / minInterval) * minInterval;
      }
    }

    step = Math.max(1, Math.round(step));
    return step;
  }
}